import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, filter, fromEvent, merge, startWith, timer } from 'rxjs';

import { LoadingSkeletonComponent } from '@shared/components/loading-skeleton';

import { AiAssistantService } from '../../ai-assistant.service';

export interface ConversationListItem {
  id: string;
  title: string;
  isActive: boolean;
}

const LOAD_MORE_SCROLL_THRESHOLD = 0.85;

@Component({
  selector: 'app-ai-conversation-list',
  imports: [NgClass, LoadingSkeletonComponent, TranslatePipe],
  templateUrl: './ai-conversation-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col overflow-auto px-4 gap-2 pb-4' },
})
export class AiConversationListComponent implements OnChanges, OnDestroy {
  readonly #elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  readonly #ngZone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);
  readonly #aiService = inject(AiAssistantService);

  readonly #afterRenderSubject$ = new Subject<void>();

  public items = input<ConversationListItem[]>([]);
  public isLoading = input<boolean>(false);
  public hasMore = input<boolean>(false);
  public loadingMore = input<boolean>(false);
  public conversationSelected = output<string>();
  public newConversationRequested = output<void>();
  public loadMore = output<void>();

  constructor() {
    this.#startListeningOnScrollPosition();
  }

  public ngOnChanges(): void {
    this.#ngZone.runOutsideAngular(() => {
      timer(0)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => {
          this.#afterRenderSubject$.next();
        });
    });
  }

  public ngOnDestroy(): void {
    this.#afterRenderSubject$.complete();
  }

  public onSelect(id: string): void {
    this.#aiService.selectConversation(id);
    this.conversationSelected.emit(id);
  }

  public onNewConversation(): void {
    this.#aiService.openSmall();
  }

  #startListeningOnScrollPosition(): void {
    this.#ngZone.runOutsideAngular(() => {
      merge(fromEvent(this.#elementRef.nativeElement, 'scroll'), this.#afterRenderSubject$.asObservable())
        .pipe(
          startWith(null),
          filter(() => this.hasMore() && !this.loadingMore()),
          filter(() => {
            const scrollHeight = this.#elementRef.nativeElement.scrollHeight;

            if (!scrollHeight) {
              return false;
            }

            const scrollPercentage =
              (this.#elementRef.nativeElement.scrollTop + this.#elementRef.nativeElement.clientHeight) / scrollHeight;

            return scrollPercentage > LOAD_MORE_SCROLL_THRESHOLD;
          }),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => {
          this.#ngZone.run(() => {
            this.loadMore.emit();
          });
        });
    });
  }
}
